import EmptyTable from '@/components/table/components/EmptyTable.tsx';
import { Plus, Tent } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { useNavigate, useSearch } from '@tanstack/react-router';
import toursApi from '@/features/dashboard/tours/api/toursApi.ts';
import Table from '@/components/table/components/Table.tsx';
import { toursApiQueryKeys } from '@/features/dashboard/tours/api/toursApi.queryKeys.ts';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import { TourStatus } from '@/features/dashboard/tours/types/TourStatus.ts';
import { RegistrationStatus } from '@/features/dashboard/tours/types/RegistrationStatus.ts';
import useActivateTour from '@/features/dashboard/tourConfig/hooks/useActivateTour.ts';
import useOpenRegistration from '@/features/dashboard/tourConfig/hooks/useOpenRegistration.ts';
import useCloseRegistration from '@/features/dashboard/tourConfig/hooks/useCloseRegistration.ts';
import useArchiveTour from '@/features/dashboard/tourConfig/hooks/useArchiveTour.ts';
import useDeleteTour from '@/features/dashboard/tourConfig/hooks/useDeleteTour.ts';
import { copyTourLinkToClipboard } from '@/features/dashboard/tours/hooks/useTourLink.ts';
import React, { useState } from 'react';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import timeUtils from '@/utils/timeUtils.ts';
import TourListDto from '@/features/dashboard/tours/types/TourListDto.ts';
import CancelTourModal from '@/features/dashboard/tours/components/CancelTourModal.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import { ListSearch } from '@/components/table/routes/listSearchParams.ts';

const isTourEndDateInPast = (tour: TourListDto): boolean =>
  new Date(tour.tourDates.end) < new Date();

const isTourEndDateInFuture = (tour: TourListDto): boolean =>
  new Date(tour.tourDates.end) >= new Date();

const TourTable = () => {
  const navigate = useNavigate();
  // Lista wyjazdów wisi pod trzema trasami (`/`, `/dashboard`, `/tours`),
  // więc wycinek da się odczytać tylko nieściśle.
  const search = useSearch({ strict: false }) as ListSearch;
  const { confirmWithCallback, confirmDeleteWithCallback } =
    confirmDialogUseHook();

  const [cancelTourTarget, setCancelTourTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const deleteTourMutation = useDeleteTour();
  const activateTourMutation = useActivateTour();
  const openRegistrationMutation = useOpenRegistration();
  const closeRegistrationMutation = useCloseRegistration();
  const archiveTourMutation = useArchiveTour();

  const tourTableRowContextMenu: RowContextMenuItemParams<TourListDto>[] = [
    {
      label: 'Szczegóły',
      action: (tour) => navigate({ to: `/app/tours/${tour.id}` }),
    },
    {
      label: 'Edytuj',
      action: (tour) => navigate({ to: `/app/tours/${tour.id}/config/basics` }),
    },
    {
      label: 'Skopiuj link rezerwacji',
      disabled: (tour) => tour.status === TourStatus.DRAFT,
      action: async (tour) => copyTourLinkToClipboard(tour.id),
    },
    {
      label: 'Aktywuj',
      visible: (tour) => tour.status === TourStatus.DRAFT,
      action: (tour) =>
        confirmWithCallback({
          title: 'Aktywuj wyjazd',
          description: (
            <>
              Czy na pewno chcesz aktywować wyjazd <b>{tour.tourName}</b>?
            </>
          ),
          onConfirm: () => activateTourMutation.mutate(tour.id),
        }),
    },
    {
      label: 'Otwórz rejestrację',
      visible: (tour) =>
        [RegistrationStatus.CLOSED, RegistrationStatus.PLANNED].includes(
          tour.registrationDetails.registrationStatus,
        ),
      disabled: (tour) =>
        tour.registrationDetails.registrationBlocked ||
        tour.status == TourStatus.DRAFT,
      action: (tour) =>
        confirmWithCallback({
          title: 'Otwórz rejestrację',
          description: tour?.registrationDates?.start ? (
            <>
              Otwarcie rejestracji na obóz <b>{tour.tourName}</b> jest
              zaplanowane na{' '}
              <b>{timeUtils.toDateTimeString(tour.registrationDates.start)}</b>.
              Czy napewno chcesz otworzyć rejestrację już teraz?
            </>
          ) : (
            <>
              Czy napewno chcesz otworzyć rejestrację na obóz{' '}
              <b>{tour.tourName}</b>?
            </>
          ),
          onConfirm: () => openRegistrationMutation.mutate(tour.id),
        }),
    },
    {
      label: 'Zamknij rejestrację',
      visible: (tour) =>
        tour.registrationDetails.registrationStatus == RegistrationStatus.OPEN,
      action: (tour) => closeRegistrationMutation.mutate(tour.id),
    },
    {
      label: 'Anuluj wyjazd',
      variant: 'destructive',
      visible: (tour) => tour.status === TourStatus.READY,
      disabled: (tour) => isTourEndDateInPast(tour),
      disabledTooltip: () => 'Nie można anulować wyjazdu po dacie zakończenia',
      action: (tour) =>
        setCancelTourTarget({
          id: tour.id,
          name: tour.tourName,
        }),
    },
    {
      label: 'Usuń',
      variant: 'destructive',
      visible: (tour) => TourStatus.DRAFT === tour.status,
      action: (tour) => deleteTourMutation.mutate(tour.id),
    },
    {
      label: 'Archiwizuj',
      visible: (tour) =>
        [TourStatus.READY, TourStatus.CANCELLED].includes(tour.status),
      disabled: (tour) => isTourEndDateInFuture(tour),
      disabledTooltip: () =>
        'Nie można zarchiwizować wyjazdu przed datą zakończenia',
      action: (tour) =>
        confirmWithCallback({
          title: 'Archiwizuj wyjazd',
          description: (
            <>
              Czy na pewno chcesz zarchiwizować wyjazd <b>{tour.tourName}</b>?
            </>
          ),
          onConfirm: () => archiveTourMutation.mutate(tour.id),
        }),
    },
  ];

  return (
    <div>
      <Table
        listSearch={search}
        queryKey={toursApiQueryKeys.all()}
        fetchData={toursApi.getTours}
        fetchTableDef={toursApi.getFieldDefinitions}
        emptyDataComponent={
          <EmptyTable
            title="Brak wyjazdów"
            description="Utwórz swój pierwszy wyjazd aby rozpocząć zbieranie zapisów"
            icon={<Tent />}
          >
            <ActionButton
              tone="outline"
              size="xs"
              onClick={() => navigate({ to: '/app/tours/new/config/basics' })}
            >
              <Plus />
              Utwórz wyjazd
            </ActionButton>
          </EmptyTable>
        }
        rowContextMenuItems={tourTableRowContextMenu}
        tagTargetType={TaggableType.TOUR}
        title="Wyjazdy"
        primaryAction={
          <ActionButton
            tone="accent"
            size="sm"
            type="button"
            onClick={() => navigate({ to: '/app/tours/new/config/basics' })}
          >
            <Plus />
            <span>Dodaj wyjazd</span>
          </ActionButton>
        }
      />
      {cancelTourTarget && (
        <CancelTourModal
          tourId={cancelTourTarget.id}
          tourName={cancelTourTarget.name}
          onClose={() => setCancelTourTarget(null)}
        />
      )}
    </div>
  );
};

export default TourTable;
